import { noop } from 'es-toolkit';
import { Queue } from '../../../utils/queue';
import { isClientRuntime } from '@skin-seoul/env-utils';

let instance: CartTokenQueue | null = null;

class CartTokenQueue extends Queue<string> {
  private readonly MAX_TOKENS = 10;

  /**
   * Adds a new token
   * @param token Token to add
   */
  override enqueue(token: string): void {
    if (!token) {
      return;
    }

    const latestToken = this.peekLatest();
    if (latestToken === token) {
      return;
    }

    super.enqueue(token);

    while (this.size() > this.MAX_TOKENS) {
      super.dequeue();
    }
  }

  /**
   * Returns the most recently added token
   * Custom implementation because Queue's peek() returns the oldest element
   */
  peekLatest(): string | undefined {
    if (this.isEmpty()) {
      return undefined;
    }

    const itemsString = this.toString();
    if (!itemsString) {
      return undefined;
    }

    const items = itemsString.split(',');
    return items[items.length - 1];
  }

  /**
   * Returns the most recent token (overriding peek method for API compatibility)
   */
  override peek(): string | undefined {
    const latestToken = this.peekLatest();

    return latestToken;
  }
}

/**
 * Function that returns a singleton instance
 * Returns a dummy implementation on the server and a real implementation on the client.
 * React Native has no `window`, so the check must be isClientRuntime(), not isServer() —
 * the legacy check silently handed RN the no-op stub and no cart token ever left the app.
 */
function getCartTokenQueue(): CartTokenQueue {
  if (!isClientRuntime()) {
    return {
      enqueue: noop,
      peek: () => undefined,
      peekLatest: () => undefined,
      dequeue: () => undefined,
      clear: noop,
      isEmpty: () => true,
      size: () => 0,
      toString: () => '',
    } as unknown as CartTokenQueue;
  }

  if (!instance) {
    instance = new CartTokenQueue();
  }

  return instance;
}

const cartTokenQueue = getCartTokenQueue();

// Expose to window for React Native WebView cart token injection
if (typeof window !== 'undefined') {
  (window as Window & { __cartTokenQueue?: CartTokenQueue }).__cartTokenQueue = cartTokenQueue;
}

export { cartTokenQueue };
